import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { PushQuoteService } from '@zero/shared/ui-push';
import { AuthStateFacade, StoreErrorHandler } from '@zero/shared/util-global-state';
import { OrderStatus } from '@zero/shared/util-models';
import { catchError, filter, map, mergeMap, of, tap } from 'rxjs';
import { OrdersInfrastructure } from '../../infrastructure/orders.infrastructure';
import { orderActions, orderApiActions } from './order.actions';
import { selectOrderByCategoryAndId, selectOrderISINsByCategory, selectOrderState } from './order.selectors';

@Injectable({ providedIn: 'root' })
export class OrderEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly storeErrorHandler = inject(StoreErrorHandler);
  private readonly ordersInfrastructure = inject(OrdersInfrastructure);
  private readonly quoteService = inject(PushQuoteService);
  private readonly authState = inject(AuthStateFacade);

  public loadOrders$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(orderActions.loadOrders),
      concatLatestFrom(({ category }) => this.store.select(selectOrderState(category))),
      filter(([{ force }, { initiallyLoaded }]) => force || !initiallyLoaded),
      tap(([{ category }]) => {
        // eslint-disable-next-line @ngrx/no-dispatch-in-effects
        this.store.dispatch(orderActions.setLoading({ category, loading: true }));
      }),
      mergeMap(([{ category, size }, { page }]) => {
        let status: OrderStatus;
        if (category === 'canceledOrders') {
          status = OrderStatus.Canceled;
        } else if (category === 'filledOrders') {
          status = OrderStatus.Filled;
        } else {
          status = OrderStatus.Open;
        }
        return this.ordersInfrastructure.getOrders(status, page, size).pipe(
          map((data) => orderApiActions.loadOrdersSuccess({ category, orders: data.list, hasNext: data.hasNext })),
          catchError(({ error }) => of(orderApiActions.loadOrdersFailure({ error, category }))),
        );
      }),
      catchError(({ error }) => of(orderApiActions.loadOrdersFailure({ error, category: null }))),
    );
  });

  public handleUserLogout$ = createEffect(() => {
    return this.authState.loggedIn$.pipe(
      filter((loggedIn) => !loggedIn),
      map(() => orderActions.unsubscribeFromOpenOrdersQuotes()),
    );
  });

  public unsubscribeFromQuotes$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(orderActions.unsubscribeFromOpenOrdersQuotes),
        concatLatestFrom(() => this.store.select(selectOrderISINsByCategory('openOrders'))),
        filter(([, isins]) => isins.length > 0),
        tap(([, isins]) => this.quoteService.unsubscribeMulti(isins)),
      );
    },
    { dispatch: false },
  );

  public unsubscribeFromOpenOrderQuote$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(orderActions.unsubscribeFromOpenOrderQuote),
        concatLatestFrom(({ orderId }) => this.store.select(selectOrderByCategoryAndId('openOrders', orderId))),
        filter(([, order]) => !!order),
        tap(([, { isin }]) => this.quoteService.unsubscribe(isin)),
      );
    },
    { dispatch: false },
  );

  public handleBackendErrors$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(orderApiActions.loadOrdersFailure),
        tap((error) => this.storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );
}
